import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  businessDayFetchRange,
  computeTimesheet,
  todayIsoParis,
  type TimeEntry,
  type Timesheet,
} from '@lemontaigu/staff';
import { useRoster, useTimesheet } from '@lemontaigu/staff/react';
import { FrDateInput } from '@lemontaigu/reception/ui/FrDateInput.js';
import { dayKey, fmtDayLabel, fmtHours, fmtTime, monthStartKey } from './logic.js';
import { MyRequests, RequestShiftForm } from './ShiftRequests.js';

// « Pointages » (S3, trimmed to self-only at APPS-REORG R2 — site
// reassessment 2026-07-14): the employee's OWN entries (RLS scopes the query;
// the 0031 open-entry widening only exposes other people's OPEN shifts, which
// the clock_in range + own-row rendering here never surfaces as foreign
// history) with the timesheet summary as a self-check. Corrections, the
// employee picker, and the printable payroll report are OPERATOR features —
// they live on bos.lemontaigu.com (apps/admin), not here.

export function Pointages() {
  // `timesheetStore`, not `timesheet` — the latter is this view's computed
  // report, below.
  const timesheetStore = useTimesheet();
  const roster = useRoster();
  const today = todayIsoParis();
  const [fromDay, setFromDay] = useState(() => monthStartKey(today));
  const [toDay, setToDay] = useState(today);
  const [myEmployeeId, setMyEmployeeId] = useState<string | null>(null);
  const [entries, setEntries] = useState<TimeEntry[] | null>(null);
  const [err, setErr] = useState('');
  // « J'ai oublié de pointer » + the « Mes demandes » list's reload key.
  const [forgetting, setForgetting] = useState(false);
  const [requestsKey, setRequestsKey] = useState(0);

  useEffect(() => {
    void (async () => {
      try {
        setMyEmployeeId(await roster.myEmployeeId());
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [roster]);

  const load = useCallback(async () => {
    setErr('');
    if (myEmployeeId === null) return;
    if (fromDay === '' || toDay === '' || fromDay > toDay) {
      setEntries([]);
      return;
    }
    // Explicit own-row filter: RLS would allow other members' OPEN entries
    // through since 0031 (the « Sur place » read) — this view is strictly
    // the caller's own record.
    // Superset window — computeTimesheet does the exact business-day filtering.
    const window = businessDayFetchRange(fromDay, toDay);
    try {
      setEntries(await timesheetStore.entriesBetween(window.fromIso, window.toIso, myEmployeeId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [myEmployeeId, fromDay, toDay, timesheetStore]);

  useEffect(() => {
    void load();
  }, [load]);

  const timesheet = useMemo(
    () => (entries === null ? null : computeTimesheet(entries, { fromDay, toDay })),
    [entries, fromDay, toDay],
  );

  return (
    <div className="pointages">
      <div className="ts-controls no-print">
        <label className="ts-field">
          <span>Du</span>
          <FrDateInput value={fromDay} max={toDay} onChange={setFromDay} />
        </label>
        <label className="ts-field">
          <span>Au</span>
          <FrDateInput value={toDay} min={fromDay} onChange={setToDay} />
        </label>
        {timesheet !== null && (
          <button type="button" className="auth-btn ts-print" onClick={() => window.print()}>
            Imprimer
          </button>
        )}
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      {myEmployeeId !== null && (
        <div className="no-print">
          {forgetting ? (
            <RequestShiftForm
              defaultDay={dayKey(new Date())}
              onSent={() => {
                setForgetting(false);
                setRequestsKey((k) => k + 1);
              }}
              onCancel={() => setForgetting(false)}
            />
          ) : (
            <button type="button" className="auth-btn pt-forgot" onClick={() => setForgetting(true)}>
              J’ai oublié de pointer
            </button>
          )}
        </div>
      )}

      {timesheet !== null && (
        <TimesheetReport timesheet={timesheet} onRequested={() => setRequestsKey((k) => k + 1)} />
      )}

      {myEmployeeId !== null && <MyRequests employeeId={myEmployeeId} refreshKey={requestsKey} />}

      <p className="staff-empty no-print">
        Une erreur dans vos pointages ? Touchez « Corriger » sur la ligne concernée, ou « J’ai oublié de
        pointer » : la direction reçoit votre demande et la valide.
      </p>
    </div>
  );
}

function TimesheetReport({ timesheet, onRequested }: { timesheet: Timesheet; onRequested: () => void }) {
  // Which of MY shifts has its correction form open (one at a time).
  const [fixing, setFixing] = useState<number | null>(null);
  return (
    <section className="ts-report">
      <header className="ts-report-head">
        <h2 className="section-title">Mon relevé d’heures</h2>
        <div className="ts-report-range">
          Du {fmtDayLabel(timesheet.fromDay)} au {fmtDayLabel(timesheet.toDay)}
        </div>
      </header>

      <div className="ts-summary">
        <div className="ts-summary-item">
          <span className="ts-summary-value">{fmtHours(timesheet.totalHours)}</span>
          <span className="ts-summary-label">heures travaillées</span>
        </div>
        <div className="ts-summary-item">
          <span className="ts-summary-value">{timesheet.daysWorked}</span>
          <span className="ts-summary-label">jours travaillés</span>
        </div>
        <div className="ts-summary-item">
          <span className="ts-summary-value">{timesheet.daysOff}</span>
          <span className="ts-summary-label">jours de repos</span>
        </div>
      </div>

      {timesheet.openEntryCount > 0 && (
        <p className="ts-warning">
          {timesheet.openEntryCount} pointage{timesheet.openEntryCount > 1 ? 's' : ''} sans départ
          sur la période.
        </p>
      )}

      {timesheet.days.length === 0 ? (
        <p className="staff-empty">Aucun pointage sur la période.</p>
      ) : (
        <table className="ts-table">
          <thead>
            <tr>
              <th>Jour</th>
              <th>Arrivée → Départ</th>
              <th className="ts-num">Heures</th>
              <th className="ts-num">Total jour</th>
              <th className="no-print" />
            </tr>
          </thead>
          <tbody>
            {timesheet.days.map((day) =>
              day.shifts.map((shift, i) => (
                <Fragment key={shift.entryId}>
                <tr className={shift.hours === null ? 'ts-open' : ''}>
                  {i === 0 && (
                    <td rowSpan={day.shifts.length} className="ts-day">
                      {fmtDayLabel(day.day)}
                    </td>
                  )}
                  <td>
                    {fmtTime(shift.clockIn)} →{' '}
                    {shift.clockOut ? fmtTime(shift.clockOut) : <em>en cours</em>}
                  </td>
                  <td className="ts-num">{shift.hours === null ? '—' : fmtHours(shift.hours)}</td>
                  {i === 0 && (
                    <td rowSpan={day.shifts.length} className="ts-num ts-day-total">
                      {fmtHours(day.totalHours)}
                    </td>
                  )}
                  <td className="no-print ts-actions">
                    {fixing !== shift.entryId ? (
                      <button type="button" onClick={() => setFixing(shift.entryId)}>
                        Corriger
                      </button>
                    ) : null}
                  </td>
                </tr>
                {fixing === shift.entryId && (
                  <tr className="no-print">
                    <td colSpan={5}>
                      <RequestShiftForm
                        shift={shift}
                        defaultDay={day.day}
                        onSent={() => {
                          setFixing(null);
                          onRequested();
                        }}
                        onCancel={() => setFixing(null)}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              )),
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
